import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import Header, { HeaderShell } from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import ViewAsBanner from '@/components/ViewAsBanner';
import ScanConfirmPage from '@/pages/ScanConfirmPage';
import InventoryPage from '@/pages/InventoryPage';
import ReorderPage from '@/pages/ReorderPage';
import ReceivePage from '@/pages/ReceivePage';
import ExportsPage from '@/pages/ExportsPage';
import AuditPage from '@/pages/AuditPage';
import LoginPage from '@/pages/LoginPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import SetPasswordPage from '@/pages/SetPasswordPage';
import ApprovePage from '@/pages/ApprovePage';
import ReviewPage from '@/pages/ReviewPage';
import ReviewDetailPage from '@/pages/ReviewDetailPage';
import ReconciliationPage from '@/pages/ReconciliationPage';
import ReconciliationCyclePage from '@/pages/ReconciliationCyclePage';
import SettingsPage from '@/pages/SettingsPage';
import PracticeProfilePage from '@/pages/PracticeProfilePage';
import VendorsSettingsPage from '@/pages/VendorsSettingsPage';
import TeamSettingsPage from '@/pages/TeamSettingsPage';
import { supabase } from '@/lib/supabase';

// ScanPage is lazy-loaded to keep html5-qrcode (~100 KB gzipped)
// out of the main bundle. Every other page is small and stays
// eager — lazy-loading them would buy ~5 KB at the cost of a
// chunk-load flicker on every navigation.
const ScanPage = lazy(() => import('@/pages/ScanPage'));
import {
  buildLoginRedirect,
  clearLogoutIntent,
  isLogoutInProgress,
  logout,
} from '@/lib/auth';
import {
  isClinicProfile,
  isClinicRole,
  readPersistedViewAsRole,
  useSessionStore,
  type ClinicRole,
  type Membership,
  type SessionValue,
} from '@/lib/session';
import { defaultPermissionsForRole } from '@/lib/permissions/catalog';
import Icon from '@/components/Icon';

// Silent-retry delay for the clinic fetch. One auto-retry covers the
// transient-network case (dropped packet, brief DNS hiccup) without
// the user seeing a retry UI at all.
const CLINIC_FETCH_RETRY_DELAY_MS = 800;

/**
 * Phone-shell frame — the mobile-first visual container used by
 * every layout. On desktop it shows as a centered phone mock;
 * on actual mobile viewports it fills the screen.
 *
 * Height handling:
 * - Outer wrapper: min-h-dvh — page background covers the full
 *   viewport even if the shell is shorter (desktop case).
 * - Inner shell: h-dvh on mobile so the shell is BOUNDED by the
 *   viewport, which lets the <main>'s overflow-y-auto actually
 *   engage. With min-h-screen, the shell would grow with its
 *   content and the BottomNav would scroll out of view.
 *   On sm+ we switch to h-[844px] for the centered phone mock.
 *
 * Why dvh, not vh: 100vh on iOS Safari is the EXPANDED viewport
 * (address bar collapsed), so the shell ends up taller than what
 * the user actually sees and BottomNav slides under the address
 * bar. 100dvh tracks the dynamic viewport — supported in iOS
 * Safari 15.4+, which is everything we target.
 */
function PhoneShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-slate-950">
      <div className="phone-shell w-full max-w-[420px] h-dvh sm:h-[844px] sm:my-6 sm:rounded-[2rem] flex flex-col bg-white overflow-hidden">
        {children}
      </div>
    </div>
  );
}

/**
 * Public layout — no Header, no BottomNav. Used for login and the
 * auth callback page where the user is (by definition) not signed in.
 */
function PublicLayout() {
  return (
    <PhoneShell>
      <main className="flex-1 overflow-y-auto no-scrollbar bg-surface flex flex-col">
        <Outlet />
      </main>
    </PhoneShell>
  );
}

// ============================================================
// AuthenticatedLayout — session + clinic state machine
// ============================================================
//
// Combines two responsibilities:
//
//   1. Auth guard (1B.2). Drives session state from supabase-js,
//      handles the initial check + listener for mid-session changes,
//      and distinguishes never-authed (preserve destination) from
//      was-authed (expired vs. intentional logout).
//
//   2. Clinic resolution (1B.3). Once authenticated, fetches the
//      user's memberships, auto-retries once on failure, and
//      provides the resolved membership to descendants via
//      Zustand store (1B.4). Multi-clinic users get "first by
//      created_at" as the default active clinic until the switcher
//      UI lands in a later ticket.
//
// Rendering states:
//
//   session === undefined                       → shell + spinner (auth loading)
//   session === null, never authed              → <Navigate> to login w/ ?next=
//   session === null, was authed (unintentional) → <Navigate> to /login?error=session_expired
//   session === null, was authed (intentional)   → <Navigate> to /login (logout)
//   session authed, clinicState: loading         → shell + spinner (clinic fetch)
//   session authed, clinicState: error           → error screen (retry + sign out)
//   session authed, clinicState: empty           → error screen "not assigned to a clinic"
//                                                   (invariant violation — should never happen
//                                                   in production; sign-out only, no retry)
//   session authed, clinicState: loaded          → populate session store + render route tree

type ClinicState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'loaded'; memberships: Membership[]; activeMembership: Membership };

type JoinedRow = {
  clinic_id: string;
  role: string;
  created_at: string;
  clinics: { name: string; profile: string };
};

function toMembership(row: JoinedRow): Membership | null {
  if (!isClinicRole(row.role)) return null;
  if (!isClinicProfile(row.clinics.profile)) return null;
  return {
    clinic_id: row.clinic_id,
    clinic_name: row.clinics.name,
    clinic_profile: row.clinics.profile,
    role: row.role,
    created_at: row.created_at,
  };
}

function AuthenticatedLayout() {
  const location = useLocation();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [hadSession, setHadSession] = useState(false);
  const [clinicState, setClinicState] = useState<ClinicState>({ kind: 'idle' });
  // Incrementing this retriggers the clinic-fetch effect for a manual
  // retry from the error screen. Cheaper than exposing imperative
  // refetch machinery through a ref.
  const [retryToken, setRetryToken] = useState(0);

  // ---- Auth state --------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      if (data.session) setHadSession(true);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (cancelled) return;
      setSession(newSession);
      if (newSession) setHadSession(true);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // ---- Clinic membership state ------------------------------------
  //
  // Runs whenever we have a fresh user or the retry token bumps.
  // Silent-retries once on failure, then surfaces the error.
  //
  // userId and email are captured as primitives so token refreshes
  // (which rotate session reference but not user identity) don't
  // re-trigger this effect — same primitive values, same dep array.
  const userId = session?.user.id;
  const email = session?.user.email;
  useEffect(() => {
    if (!userId) {
      setClinicState({ kind: 'idle' });
      return;
    }

    let cancelled = false;
    let retryTimeoutId: number | undefined;

    const run = async (attempt: number): Promise<void> => {
      if (cancelled) return;
      setClinicState({ kind: 'loading' });

      const { data, error } = await supabase
        .from('clinic_members')
        .select('clinic_id, role, created_at, clinics(name, profile)')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (cancelled) return;

      if (error) {
        if (attempt === 1) {
          // Silent retry. User sees continuous spinner rather than
          // a "something went wrong" flash for transient failures.
          retryTimeoutId = window.setTimeout(() => {
            void run(2);
          }, CLINIC_FETCH_RETRY_DELAY_MS);
          return;
        }
        console.error('Clinic membership fetch failed after retry:', error);
        setClinicState({ kind: 'error', message: error.message });
        return;
      }

      const memberships = (data ?? [])
        .map((row) => toMembership(row as JoinedRow))
        .filter((m): m is Membership => m !== null);

      if (memberships.length === 0) {
        // Invariant violation: an authenticated user should always
        // have at least one clinic membership. Surface loudly.
        console.error(
          `[viashift] User ${userId} has no clinic memberships. ` +
            'Every authenticated user must be attached to a clinic ' +
            '— this is an onboarding bug.',
        );
        setClinicState({ kind: 'empty' });
        return;
      }

      // Memberships are sorted ASC by created_at via the query, so
      // the first row is the "first by membership date" default.
      const activeMembership = memberships[0];
      if (!activeMembership) {
        // Unreachable — length check above guarantees at least one
        // element. Guarded for noUncheckedIndexedAccess.
        setClinicState({ kind: 'empty' });
        return;
      }

      // Compute the permission cache. Base set comes from the real
      // role's catalog defaults. If the real role can impersonate
      // AND sessionStorage has a valid persisted target, fold the
      // impersonation in before populating the store — this prevents
      // a single-frame flash of the real-role view when reloading a
      // page that was in demo mode.
      const realRole: ClinicRole = activeMembership.role;
      const basePerms = defaultPermissionsForRole(realRole);
      let viewAsRole: ClinicRole | null = null;
      let permissions = basePerms;

      if (basePerms.has('system.view_as_role')) {
        const persisted = readPersistedViewAsRole(realRole);
        if (persisted !== null) {
          viewAsRole = persisted;
          permissions = defaultPermissionsForRole(persisted);
        }
      }
      // No `else` cleanup: readPersistedViewAsRole self-clears
      // invalid storage values. When the real role lacks the
      // view-as permission, we leave any orphan storage entry
      // alone (e.g. owner-then-staff role downgrade scenario);
      // re-entering the owner role on a future session re-reads
      // and revalidates it. Cheap to be permissive.

      const sessionValue: SessionValue = {
        userId,
        email: email ?? '',
        memberships,
        activeMembership,
        permissions,
        viewAsRole,
      };
      useSessionStore.setState({ session: sessionValue });
      setClinicState({ kind: 'loaded', memberships, activeMembership });
    };

    void run(1);

    return () => {
      cancelled = true;
      if (retryTimeoutId !== undefined) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [userId, email, retryToken]);

  const onRetry = useCallback(() => setRetryToken((t) => t + 1), []);

  const onSignOut = useCallback(() => {
    // Shared with Header (1B.6) — sets the LOGOUT_INTENT_KEY flag
    // before signOut so the auth guard's SIGNED_OUT handler skips
    // the "session expired" banner and routes cleanly to /login.
    void logout();
  }, []);

  // ---- Render: auth phase -----------------------------------------

  // 1a. Auth still loading.
  if (session === undefined) {
    return <LoadingShell />;
  }

  // 1b. Unauthenticated.
  if (session === null) {
    if (hadSession) {
      const intentional = isLogoutInProgress();
      if (intentional) clearLogoutIntent();
      return (
        <Navigate
          to={intentional ? '/login' : '/login?error=session_expired'}
          replace
        />
      );
    }
    const targetPath = location.pathname + location.search;
    return <Navigate to={buildLoginRedirect(targetPath)} replace />;
  }

  // ---- Render: clinic phase (session is now Session) --------------

  // 2a. Clinic fetch still loading (or idle, briefly, between auth
  //     resolving and the effect firing).
  if (clinicState.kind === 'idle' || clinicState.kind === 'loading') {
    return <LoadingShell />;
  }

  // 2b. Fetch failed after one silent retry.
  if (clinicState.kind === 'error') {
    return (
      <ErrorShell
        title="Couldn't load your clinic"
        message="Something went wrong connecting to the server. Check your connection and try again."
        detail={clinicState.message}
        primaryLabel="Retry"
        onPrimary={onRetry}
        onSignOut={onSignOut}
      />
    );
  }

  // 2c. Invariant violation — authenticated user has no clinic.
  //     Treated as a hard error: no retry (retrying won't invent
  //     memberships), only sign-out.
  if (clinicState.kind === 'empty') {
    return (
      <ErrorShell
        title="Your account isn't connected to a clinic"
        message="Contact your clinic admin — this shouldn't happen. Sign out and try again once your admin has added you."
        onSignOut={onSignOut}
      />
    );
  }

  // 3. Loaded — render the route tree. The Zustand store was
  //    populated alongside the clinicState transition above, so
  //    Header's useSession() read here is already valid.
  //    Suspense boundary at the Outlet keeps Header + BottomNav
  //    painted during a lazy chunk fetch (e.g. first nav to
  //    /scan) — only the body shows the spinner.
  //
  //    ViewAsBanner renders above Header when viewAsRole is set,
  //    is a no-op render otherwise (self-gated in the component).
  return (
    <PhoneShell>
      <ViewAsBanner />
      <Header />
      <main className="flex-1 overflow-y-auto no-scrollbar bg-surface">
        <Suspense
          fallback={
            <div className="h-full flex items-center justify-center">
              <GuardSpinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>
      <BottomNav />
    </PhoneShell>
  );
}

// ---- Shell sub-components ------------------------------------------

function LoadingShell() {
  return (
    <PhoneShell>
      <HeaderShell clinicName="" roleIcon="user" roleLabel="" muted />
      <main className="flex-1 overflow-y-auto no-scrollbar bg-surface flex items-center justify-center">
        <GuardSpinner />
      </main>
      <BottomNav />
    </PhoneShell>
  );
}

interface ErrorShellProps {
  title: string;
  message: string;
  /** Extra technical detail shown in muted type — e.g. the Supabase error message. */
  detail?: string;
  primaryLabel?: string;
  onPrimary?: () => void;
  onSignOut: () => void;
}

function ErrorShell({
  title,
  message,
  detail,
  primaryLabel,
  onPrimary,
  onSignOut,
}: ErrorShellProps) {
  return (
    <PhoneShell>
      <main className="flex-1 flex flex-col items-center justify-center p-6 bg-surface text-center">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
          <Icon
            name="shield"
            className="w-6 h-6 text-amber-600"
            strokeWidth={2}
          />
        </div>
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-[300px] leading-relaxed">
          {message}
        </p>
        {detail && (
          <p className="text-[11px] text-slate-400 mt-3 max-w-[300px] font-mono break-words">
            {detail}
          </p>
        )}
        <div className="mt-6 w-full max-w-[280px] flex flex-col gap-2">
          {primaryLabel && onPrimary && (
            <button
              type="button"
              onClick={onPrimary}
              className="w-full h-11 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 transition text-sm font-semibold text-white"
            >
              {primaryLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onSignOut}
            className="w-full h-11 rounded-xl bg-slate-100 hover:bg-slate-200 active:bg-slate-300 transition text-sm font-medium text-slate-800"
          >
            Sign out
          </button>
        </div>
      </main>
    </PhoneShell>
  );
}

/**
 * Body-sized spinner for the loading state.
 */
function GuardSpinner() {
  return (
    <span
      className="w-8 h-8 border-[3px] border-slate-200 border-t-green-600 rounded-full animate-spin"
      role="status"
      aria-label="Loading"
    />
  );
}

/**
 * App shell. Route layout split (1B.1) separates public routes from
 * authenticated ones. Session guard (1B.2) + clinic resolution (1B.3)
 * both live in AuthenticatedLayout.
 */
function App() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/approve" element={<ApprovePage />} />
      </Route>

      <Route element={<AuthenticatedLayout />}>
        <Route path="/" element={<Navigate to="/scan" replace />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/scan/confirm/:mode" element={<ScanConfirmPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/reorder" element={<ReorderPage />} />
        <Route path="/receive/:orderId" element={<ReceivePage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/review/:id" element={<ReviewDetailPage />} />
        <Route path="/reconciliation" element={<ReconciliationPage />} />
        <Route path="/reconciliation/:id" element={<ReconciliationCyclePage />} />
        <Route path="/exports" element={<ExportsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/profile" element={<PracticeProfilePage />} />
        <Route path="/settings/vendors" element={<VendorsSettingsPage />} />
        <Route path="/settings/team" element={<TeamSettingsPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/scan" replace />} />
    </Routes>
  );
}

export default App;
